import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, ConfigProvider, Popconfirm, Space, Spin, Table, Tag, Tooltip, message } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchSystemServicesStatus,
  getSystemServiceStatusColor,
  getSystemServiceStatusLabel,
  restartSystemService,
} from '../services/systemServicesApi';
import '../styles/settings.css';

const AUTO_REFRESH_MS = 15000;
const MOBILE_BREAKPOINT = 768;

const renderServiceStatus = (status, record) => (
  <Space direction="vertical" size={0}>
    <Tag color={getSystemServiceStatusColor(status)}>
      {getSystemServiceStatusLabel(status)}
    </Tag>
    {record.sub_state ? (
      <span className="sett-services-meta">{record.sub_state}</span>
    ) : null}
  </Space>
);

const renderServiceExtraInfo = (extraInfo, record) => (
  <div className="sett-services-extra">
    <div>{extraInfo || '—'}</div>
    {record.main_pid ? (
      <div className="sett-services-meta">PID: {record.main_pid}</div>
    ) : null}
    {record.systemctl_error ? (
      <div className="sett-services-error">{record.systemctl_error}</div>
    ) : null}
  </div>
);

const SettingsServices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restartingId, setRestartingId] = useState(null);
  const [supervisorError, setSupervisorError] = useState(null);
  const [managerHealth, setManagerHealth] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadServices = useCallback(async ({ silent = false } = {}) => {
    if (refreshInFlightRef.current) {
      return;
    }
    refreshInFlightRef.current = true;
    if (!silent) {
      setLoading(true);
    }
    try {
      const payload = await fetchSystemServicesStatus(true);
      setRows(Array.isArray(payload?.services) ? payload.services : []);
      setManagerHealth(payload?.manager_health || null);
      setSupervisorError(null);
      setLastUpdated(payload?.timestamp || new Date().toISOString());
    } catch (error) {
      const detail = error?.response?.data?.detail
        || error?.message
        || 'Не удалось получить статус служб';
      setSupervisorError(String(detail));
      if (!silent) {
        message.error(`Supervisor: ${detail}`);
      }
    } finally {
      refreshInFlightRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadServices();
    const timer = window.setInterval(() => {
      loadServices({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [loadServices]);

  const handleRestart = useCallback(async (serviceId, unit) => {
    setRestartingId(serviceId);
    try {
      const result = await restartSystemService(serviceId);
      if (result?.success) {
        message.success(`Служба ${unit} перезапущена`);
      } else {
        message.warning(result?.stderr || `Не удалось перезапустить ${unit}`);
      }
      await loadServices({ silent: true });
    } catch (error) {
      const detail = error?.response?.data?.detail
        || error?.response?.data?.stderr
        || error?.message
        || 'Ошибка перезапуска';
      message.error(String(detail));
    } finally {
      setRestartingId(null);
    }
  }, [loadServices]);

  const renderRestartButton = useCallback((record, { block = false } = {}) => (
    <Popconfirm
      title={`Перезапустить ${record.unit}?`}
      okText="Да"
      cancelText="Нет"
      onConfirm={() => handleRestart(record.id, record.unit)}
    >
      <Button
        size="small"
        block={block}
        loading={restartingId === record.id}
        disabled={Boolean(restartingId) && restartingId !== record.id}
      >
        Перезапуск
      </Button>
    </Popconfirm>
  ), [handleRestart, restartingId]);

  const columns = useMemo(() => [
    {
      title: 'Служба',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (title, record) => (
        <div>
          <div>{title}</div>
          {record.optional ? (
            <Tag color="default" style={{ marginTop: 4 }}>опционально</Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 240,
      responsive: ['md'],
      render: (unit, record) => (
        <Tooltip title={record.description}>
          <code>{unit}</code>
        </Tooltip>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => renderServiceStatus(status, record),
    },
    {
      title: 'Дополнительная информация',
      dataIndex: 'extra_info',
      key: 'extra_info',
      responsive: ['lg'],
      render: (extraInfo, record) => renderServiceExtraInfo(extraInfo, record),
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      align: 'right',
      fixed: 'right',
      render: (_, record) => renderRestartButton(record),
    },
  ], [renderRestartButton]);

  const lastUpdatedLabel = lastUpdated
    ? new Date(lastUpdated).toLocaleString('ru-RU')
    : null;

  return (
    <ConfigProvider locale={ruRU}>
      <div className="sett-services-page">
        <div className="sett-header">
          <h1 className="sett-header__title">Службы</h1>
          <div className="sett-header__actions" style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              className={`sett-header__action-btn sett-header__action-btn--secondary${loading ? ' sett-header__action-btn--loading' : ''}`}
              onClick={() => loadServices()}
              title="Обновить"
              aria-label="Обновить"
              tabIndex={0}
            >
              <ReloadOutlined />
            </button>
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          className="sett-services-alert"
          message={isMobile
            ? 'Статус через service-supervisor (отдельный процесс)'
            : 'Статус читается через service-supervisor (отдельный процесс)'}
          description={isMobile ? (
            lastUpdatedLabel ? `Обновлено: ${lastUpdatedLabel}` : null
          ) : (
            <>
              Страница работает даже при зависшем proxy-manager: nginx проксирует
              {' '}
              <code>/api/v1/system-services/</code>
              {' '}
              на service-supervisor (:8097).
              {lastUpdatedLabel ? (
                <>
                  {' '}
                  Обновлено:
                  {' '}
                  {lastUpdatedLabel}
                </>
              ) : null}
            </>
          )}
        />

        {supervisorError ? (
          <Alert
            type="error"
            showIcon
            className="sett-services-alert"
            message="Supervisor недоступен"
            description={supervisorError}
          />
        ) : null}

        {managerHealth && !managerHealth.reachable ? (
          <Alert
            type="warning"
            showIcon
            className="sett-services-alert"
            message="Backend API не отвечает на /health"
            description={managerHealth.error || `HTTP ${managerHealth.status_code || '—'}`}
          />
        ) : null}

        {isMobile ? (
          <Spin spinning={loading}>
            <div className="sett-services-mobile">
              {rows.map((record) => (
                <article key={record.id} className="sett-services-card">
                  <div className="sett-services-card__head">
                    <div className="sett-services-card__title-wrap">
                      <div className="sett-services-card__title">{record.title}</div>
                      {record.optional ? (
                        <Tag color="default" className="sett-services-card__optional">опционально</Tag>
                      ) : null}
                    </div>
                    {renderServiceStatus(record.status, record)}
                  </div>

                  <Tooltip title={record.description}>
                    <code className="sett-services-card__unit">{record.unit}</code>
                  </Tooltip>

                  {renderServiceExtraInfo(record.extra_info, record)}

                  <div className="sett-services-card__actions">
                    {renderRestartButton(record, { block: true })}
                  </div>
                </article>
              ))}
            </div>
          </Spin>
        ) : (
          <Table
            className="sett-table sett-services-table"
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={false}
            size="middle"
            scroll={{ x: 960 }}
          />
        )}
      </div>
    </ConfigProvider>
  );
};

export default SettingsServices;
